/**
 * Diagnostics mode API.
 *
 * Questions are loaded from:
 *   - Static JSON  : /public/data/diagnostics/<filename>  (e.g. snop.json for diag1)
 *   - Dynamic API  : questions generator service (Cloud Run), with localStorage cache
 *
 * There is NO backend call to "start" a session or fetch questions from localhost.
 * Session persistence lives entirely in Firestore (sensei-diagnostics DB).
 * The AI analysis call goes to NEXT_PUBLIC_DIAGNOSIS_API after answers are completed.
 *
 * This mirrors the React app's loadDiagnosticData() in src/lib/data/diagnostics.ts exactly.
 */

import type { DiagnosticQuestion, AnalysisResponse } from "./types";
import type { DiagnosticTemplate } from "./data";

/** Cloud Run endpoint that generates questions for a given topic */
const QUESTIONS_GENERATOR_URL =
  "https://sensei-diagnosis-questions-generator-agent-git-214011868588.us-west1.run.app";

// ─── Internal shapes matching the JSON file / generator response ──────────────

interface CategoryData {
  category:  string;
  questions: string[];
}

interface DiagnosticDataFile {
  topic:                   string;
  description:             string;
  assessment:              CategoryData[];
  final_score:             number;
  score_statement:         string;
  scmdojo_recommendations?: unknown[];
}

function defaultOptions() {
  return [
    { value: 1, label: "Strongly Disagree" },
    { value: 2, label: "Disagree"           },
    { value: 3, label: "Neutral / N.A"      },
    { value: 4, label: "Agree"              },
    { value: 5, label: "Strongly Agree"     },
  ];
}

function transformToQuestions(
  diagnosticId: string,
  assessment:   CategoryData[]
): DiagnosticQuestion[] {
  const questions: DiagnosticQuestion[] = [];
  let counter = 1;
  assessment.forEach((cat) => {
    cat.questions.forEach((text) => {
      questions.push({
        id:       `${diagnosticId}_q${counter++}`,
        text,
        category: cat.category,
        options:  defaultOptions(),
      });
    });
  });
  return questions;
}

// ─── Public: load questions ───────────────────────────────────────────────────

/**
 * Load questions for a diagnostic template.
 * - Static JSON  : fetched from /data/diagnostics/<data>
 * - Dynamic      : fetched from the questions generator API (localStorage cached)
 * - No data      : returns []
 *
 * Mirrors React's loadDiagnosticData() exactly.
 */
export async function loadDiagnosticQuestions(
  diagnostic: DiagnosticTemplate
): Promise<DiagnosticQuestion[]> {
  if (!diagnostic.data || diagnostic.data === "") {
    return [];
  }

  // ── Dynamic: call questions generator, cache in localStorage ────────────
  if (diagnostic.data === "dynamic") {
    const cacheKey = `diagnostic_data_${diagnostic.title}`;

    // Check for forced refresh param (same as React)
    const shouldRefresh =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("refresh") === "1";

    if (!shouldRefresh && typeof window !== "undefined") {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          const jsonData: DiagnosticDataFile = JSON.parse(cached);
          if (jsonData.assessment && Array.isArray(jsonData.assessment)) {
            return transformToQuestions(diagnostic.id, jsonData.assessment);
          }
        } catch {
          localStorage.removeItem(cacheKey);
        }
      }
    }

    const fd = new FormData();
    fd.append("topic", diagnostic.title);
    const res = await fetch(QUESTIONS_GENERATOR_URL, { method: "POST", body: fd });
    if (!res.ok) throw new Error(`Questions generator error: ${res.status}`);

    const jsonData: DiagnosticDataFile = await res.json();

    if (typeof window !== "undefined") {
      localStorage.setItem(cacheKey, JSON.stringify(jsonData));
    }

    return transformToQuestions(diagnostic.id, jsonData.assessment);
  }

  // ── Static JSON file ─────────────────────────────────────────────────────
  const res = await fetch(`/data/diagnostics/${diagnostic.data}`);
  if (!res.ok) {
    console.warn(`[diagnostics] Failed to load ${diagnostic.data}: ${res.status}`);
    return [];
  }
  const jsonData: DiagnosticDataFile = await res.json();
  return transformToQuestions(diagnostic.id, jsonData.assessment);
}

// ─── Public: call AI analysis engine ─────────────────────────────────────────

export interface BuildOutputArgs {
  topic:       string;
  description: string;
  questions:   DiagnosticQuestion[];
  answers:     Record<string, number>;
}

/** Build the result_json payload expected by the analysis engine */
export function buildResultJson(args: BuildOutputArgs) {
  const { topic, description, questions, answers } = args;
  const categoryMap = new Map<string, { questions: string[]; answers: number[] }>();

  questions.forEach((q) => {
    const cat = q.category ?? "General";
    if (!categoryMap.has(cat)) categoryMap.set(cat, { questions: [], answers: [] });
    const cd = categoryMap.get(cat)!;
    cd.questions.push(q.text.length > 150 ? q.text.slice(0, 147) + "…" : q.text);
    cd.answers.push(answers[q.id] ?? 0);
  });

  const assessment = Array.from(categoryMap.entries()).map(([category, cd]) => ({
    category,
    questions: cd.questions,
    answers:   cd.answers,
  }));

  const total      = Object.values(answers).reduce((s, v) => s + v, 0);
  const finalScore = questions.length > 0
    ? Math.round((total / (questions.length * 5)) * 100)
    : 0;

  return {
    topic,
    description: description.length > 500 ? description.slice(0, 497) + "…" : description,
    assessment,
    final_score: finalScore,
    score_statement: "",
  };
}

/**
 * Call the AI analysis engine (NEXT_PUBLIC_DIAGNOSIS_API) with the completed answers.
 * Mirrors React's handleAiActionPlan() in DiagnosticsResultsPage.tsx exactly.
 *
 * Returns the raw analysis JSON that gets stored in DiagnosticsSession.analysis.
 */
export async function runAiAnalysis(
  resultJson: ReturnType<typeof buildResultJson>
): Promise<AnalysisResponse> {
  const apiUrl = process.env.NEXT_PUBLIC_DIAGNOSIS_API;
  if (!apiUrl) throw new Error("NEXT_PUBLIC_DIAGNOSIS_API is not configured");

  // Guard against empty or malformed payloads that crash the backend
  if (!resultJson.assessment || resultJson.assessment.length === 0) {
    throw new Error("No assessment data to analyse — all categories are empty.");
  }

  const sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;

  const formData = new FormData();
  formData.append("result_json", JSON.stringify(resultJson));
  formData.append("session_id",  sessionId);

  const res = await fetch(apiUrl, {
    method: "POST",
    body:    formData,
    signal:  AbortSignal.timeout(160_000), // 160s — Cloud Run cold starts can take a while
  });

  if (!res.ok) {
    // Read the server's actual error message instead of just the status code
    let detail = res.statusText;
    try {
      const body = await res.text();
      if (body) {
        // Try to extract a human-readable message from JSON error bodies
        try {
          const parsed = JSON.parse(body);
          detail = parsed.detail || parsed.message || parsed.error || body;
        } catch {
          detail = body.slice(0, 300); // fallback: first 300 chars of raw text
        }
      }
    } catch { /* response body unreadable — fall through with statusText */ }
    throw new Error(`Analysis API error: ${res.status} — ${detail}`);
  }

  return res.json() as Promise<AnalysisResponse>;
}
